import { useState, useMemo } from 'react'
import { useData } from '../../core/contexts/DataContext'
import { getMeetingDateStr } from '../../utils/dateUtils'
import { Exhibitor } from '../../core/types/Exhibitor.model'
import MyMeeting from './components/MyMeeting'


interface MyMeetingsProps {
  handleNavigateTo: (route: 'schedule') => void;
}

const MyMeetings = ({ handleNavigateTo }: MyMeetingsProps) => {
  const { exhibitors, exhibitorsMeetingsDates, todayDateOption, myMeetings, downloadReport } = useData()
  const [selectedDate, setSelectedDate] = useState<string | null>('all')
  const [selectedExhibitor, setSelectedExhibitor] = useState<null | Exhibitor>(null)

  const filteredMeetings = useMemo(() => {
    return myMeetings.filter(meeting => {
      const dateMatch = selectedDate === 'all' || getMeetingDateStr(meeting.startTime) === selectedDate
      const exhibitorMatch = selectedExhibitor === null || meeting.exhibitorId === selectedExhibitor.id
      return dateMatch && exhibitorMatch
    }).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
  }, [myMeetings, selectedDate, selectedExhibitor])

  const exhibitorOptions = useMemo(() => {
    const exhibitorsIds = myMeetings.map(meeting => meeting.exhibitorId);
    const uniqueExhibitors = exhibitors.filter((exhibitor, index, self) => exhibitorsIds.includes(exhibitor.id)
    )
    return uniqueExhibitors
  }, [myMeetings, exhibitors])

  const handleSelectedExhibitor = (exhibitorId: string) => {
    if (exhibitorId === 'all') {
      setSelectedExhibitor(null)
    } else {
      const exhibitor = exhibitors.find(ex => ex.id === parseInt(exhibitorId))
      setSelectedExhibitor(exhibitor || null)
    }
  }

  const nearestDatesMeetingsCount = useMemo(() => {
    const filterByDate = todayDateOption || exhibitorsMeetingsDates[0];
    if (!filterByDate) return 0;
    return myMeetings.filter(m => getMeetingDateStr(m.startTime) === filterByDate).length;
  }, [myMeetings, todayDateOption, exhibitorsMeetingsDates]);

  const datesWithMeetingsCount = useMemo(() => {
    const uniqueDates = Array.from(new Set(myMeetings.map(m => getMeetingDateStr(m.startTime))));
    return uniqueDates.length;
  }, [myMeetings]);



  const navigateToSchedulingMeeting = () => handleNavigateTo('schedule');


  return (
    <div className="row g-4">


      <div className="col-12">
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
              <h2 className="h3 w-100 fw-bold text-dark m-0">My Booked Meetings</h2>
              <div className="w-100 text-end">
                <button
                  onClick={downloadReport}
                  className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                >
                  <i className="bi bi-filetype-pdf"></i>
                  <span>Generate Participant Meeting Report</span>
                </button>
              </div>
            </div>

            <div className="row g-3 mb-4">
              {/* Date Filter */}
              <div className="col-md-6">
                <label className="form-label fw-medium text-muted">
                  Filter by Date
                </label>
                <select
                  value={selectedDate ?? 'all'}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="form-select"
                >
                  <option key="all" value="all">All Dates</option>
                  {exhibitorsMeetingsDates.map((date) => (
                    <option key={date} value={date}>
                      {date}
                    </option>
                  ))}
                </select>
              </div>

              {/* Exhibitor Filter */}
              <div className="col-md-6">
                <label className="form-label fw-medium text-muted">
                  Filter by Exhibitor
                </label>
                <select
                  value={selectedExhibitor?.id || 'all'}
                  onChange={(e) => handleSelectedExhibitor(e.target.value)}
                  className="form-select"
                >
                  <option key="all" value="all">All Exhibitors</option>
                  {exhibitorOptions.map((exhibitor) => (
                    <option key={exhibitor.id} value={exhibitor.id}>
                      {exhibitor.origName} (Booth {exhibitor.boothNo})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <ul className="text-muted mb-0 pb-4">
                <li>If you wish to change meeting, please cancel your current booking and make a new one.</li>
              </ul>
            </div>
            {/* Meetings Count */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="text-muted mb-0">
                Showing {filteredMeetings.length} of {myMeetings.length} meetings
              </p>
              {filteredMeetings.length > 0 && (
                <span className="badge bg-primary">
                  {filteredMeetings.length} meeting{filteredMeetings.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Meetings List */}
            {filteredMeetings.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-calendar-x display-1 text-muted"></i>
                <h4 className="text-muted mt-3">No meetings found</h4>
                <p className="text-muted">
                  {myMeetings.length === 0
                    ? "You haven't booked any meetings yet."
                    : "No meetings match your current filters."
                  }
                </p>
                {myMeetings.length === 0 && (
                  <button onClick={navigateToSchedulingMeeting} className="btn btn-primary">
                    Book Your First Meeting
                  </button>
                )}
              </div>
            ) : (
              <div className="row g-3">
                {filteredMeetings.map((meeting, index) => (<MyMeeting key={meeting.id} meeting={meeting} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      {myMeetings.length > 0 && (
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h4 className="h5 fw-semibold text-dark mb-3">Meeting Summary</h4>
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="h4 fw-bold text-primary mb-1">{myMeetings.length}</div>
                    <div className="small text-muted">Total Meetings</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="h4 fw-bold text-success mb-1">
                      {exhibitorOptions.length}
                    </div>
                    <div className="small text-muted">Different Exhibitors</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="h4 fw-bold text-info mb-1">
                      {datesWithMeetingsCount}
                    </div>
                    <div className="small text-muted">Days with Meetings</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="h4 fw-bold text-warning mb-1">
                      {nearestDatesMeetingsCount}
                    </div>
                    <div className="small text-muted">{todayDateOption ? 'Today Meetings' : 'Day 1 Meetings'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MyMeetings 